import { useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import MusicNoteOutlinedIcon from "@mui/icons-material/MusicNoteOutlined";
import useUploadCreativeAudioMutation from "api/hooks/useUploadCreativeAudioMutation";
import { extractApiErrorMessage } from "utils/util";
import DSBox from "views/system-design/DSBox";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSIconButton from "views/system-design/DSIconButton";
import DSSecondaryButton from "views/system-design/DSSecondaryButton";
import DSTypography from "views/system-design/DSTypography";
import {
  AUDIO_ACCEPT,
  MAX_AUDIO_BYTES,
  formatSeconds,
} from "../utils/creativeOptions";

export type AudioTrack = {
  key: string;
  name: string;
  /** Measured here; the worker measures the file again and uses its own value. */
  durationSec: number;
  previewUrl: string;
  status: "uploading" | "done" | "error";
  url?: string;
  error?: string;
};

type Props = {
  track: AudioTrack | null;
  onChange: (update: (track: AudioTrack | null) => AudioTrack | null) => void;
  /** Shown under the track, e.g. when it is too long for the chosen output. */
  problem?: string | null;
  disabled?: boolean;
};

let nextKey = 0;

const readDuration = (url: string) =>
  new Promise<number>((resolve, reject) => {
    const audio = new Audio();
    audio.preload = "metadata";
    audio.onloadedmetadata = () =>
      Number.isFinite(audio.duration) && audio.duration > 0
        ? resolve(audio.duration)
        : reject(new Error("unknown length"));
    audio.onerror = () => reject(new Error("cannot play"));
    audio.src = url;
  });

/**
 * Optional soundtrack for a video or klydo animation. The file is measured and
 * uploaded as soon as it is picked, so the job only carries its URL. The form
 * owns the track (it outlives this field when the output type changes), so it
 * frees the preview when the track goes away; this field frees it on Remove.
 */
const CreativeAudioTrack = ({ track, onChange, problem, disabled }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [rejected, setRejected] = useState<string | null>(null);
  // Blocks a second pick while the first is measured, so its preview is not lost.
  const [measuring, setMeasuring] = useState(false);
  const { mutateAsync: upload } = useUploadCreativeAudioMutation();

  const update = (key: string, patch: Partial<AudioTrack>) =>
    onChange((current) =>
      current?.key === key ? { ...current, ...patch } : current,
    );

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setRejected(null);
    if (file.size > MAX_AUDIO_BYTES) {
      setRejected(`${file.name} is larger than 15 MB`);
      return;
    }
    const previewUrl = URL.createObjectURL(file);
    let durationSec: number;
    setMeasuring(true);
    try {
      durationSec = await readDuration(previewUrl);
    } catch {
      URL.revokeObjectURL(previewUrl);
      setRejected(
        `${file.name} could not be played. Use an MP3, M4A or WAV file.`,
      );
      return;
    } finally {
      setMeasuring(false);
    }
    const key = `audio-${(nextKey += 1)}`;
    onChange(() => ({
      key,
      name: file.name,
      durationSec,
      previewUrl,
      status: "uploading",
    }));
    upload(file)
      .then((url) => update(key, { status: "done", url }))
      .catch((error) =>
        update(key, {
          status: "error",
          error: extractApiErrorMessage(error, "Upload failed"),
        }),
      );
  };

  const remove = () => {
    if (track) URL.revokeObjectURL(track.previewUrl);
    onChange(() => null);
  };

  return (
    <DSBox>
      {!track && (
        <DSSecondaryButton
          size="small"
          startIcon={<MusicNoteOutlinedIcon />}
          disabled={disabled || measuring}
          onClick={() => inputRef.current?.click()}
        >
          Add an audio track
        </DSSecondaryButton>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={AUDIO_ACCEPT}
        hidden
        onChange={(event) => {
          pick(event.target.files?.[0]);
          event.target.value = "";
        }}
      />

      {track && (
        <DSBox
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1.5,
            p: 1.5,
            maxWidth: 640,
            borderRadius: 2,
            border: "2px solid",
            borderColor:
              track.status === "error" || problem ? "error.main" : "divider",
          }}
        >
          <MusicNoteOutlinedIcon />
          <DSBox sx={{ minWidth: 0, flex: "1 1 160px" }}>
            <DSTypography
              variant="body2"
              title={track.name}
              sx={{
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {track.name}
            </DSTypography>
            <DSTypography variant="caption" color="text.secondary">
              {formatSeconds(track.durationSec)} seconds
              {track.status === "uploading" && " · uploading…"}
            </DSTypography>
          </DSBox>
          <audio
            controls
            src={track.previewUrl}
            style={{ height: 36, maxWidth: "100%" }}
          />
          {track.status === "uploading" && <DSCircularProgress size={20} />}
          <DSIconButton
            size="small"
            onClick={remove}
            disabled={disabled}
            title="Remove"
          >
            <CloseIcon fontSize="small" />
          </DSIconButton>
        </DSBox>
      )}

      {(rejected || problem || track?.status === "error") && (
        <DSTypography
          variant="caption"
          color="error"
          sx={{ display: "block", mt: 0.5 }}
        >
          {rejected ||
            problem ||
            `${track?.error ?? "Upload failed"}. Remove it to continue.`}
        </DSTypography>
      )}
    </DSBox>
  );
};

export default CreativeAudioTrack;
